import React, { useState, useEffect } from 'react';
import { Stepper } from'@shared/components/Stepper';
import { SelectComplaintStep } from'./stepComponents/SelectComplaintsStep';
import { ComplaintDetailsStep } from'./stepComponents/ComplaintDetailsStep';
import { EvidenceStep } from'./stepComponents/EvidenceStep';
import { ReviewSubmitStep } from'./stepComponents/ReviewSubmitStep';
import { useNavigate, useSearchParams, useLocation } from'react-router-dom';
import { ArrowLeft, User, MapPin } from'lucide-react';
import { PageHeader } from'@shared/components/PageHeader';
import { SimpleSuccessScreen } from '@shared/components/SimpleSuccessScreen';
import { SelectAccountsStep } from '../../../../shared/components/SelectAccountsStep';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { ActiveAccountBanner } from '../../../../shared/components/ActiveAccountBanner';
import { useTranslation } from 'react-i18next';

export const CreateComplaint = () => {
 const { t } = useTranslation();
 const navigate = useNavigate();
 const location = useLocation();
 const [isSubmitted, setIsSubmitted] = useState(false);
 const [searchParams] = useSearchParams();
 
 const name = searchParams.get('name');
 const accountNo = searchParams.get('accountNo');
 const address = searchParams.get('address');
 const consumerInfo = name && accountNo && address ? { name, accountNo, address } : null;

 const showAccountSelection = searchParams.get('multiAccount') ==='true';
 
 useEffect(() => {
  return () => {
   localStorage.removeItem('complaint-request-draft');
  };
 }, []);
 
 const baseSteps = [
 {
  title: t("Select Complaint"),
  slug:"select-complaint",
  component: <SelectComplaintStep requestType="Complaint"/>,
  optional: false,
  infoTitle: t("Select Complaint"),
  infoDescription: t("Browse and select the type of complaint"),
 },
 {
  title: t("Evidence & Attachments"),
  slug:"evidence",
  component: <EvidenceStep />,
  optional: true,
  infoTitle: t("Evidence & Attachments"),
  infoDescription: t("Upload any photos or documents related to your issue"),
 },
 {
  title: t("Review & Submit"),
  slug:"review-submit",
  component: <ReviewSubmitStep />,
  optional: false,
  infoTitle: t("Review & Submit"),
  infoDescription: t("Review your complaint details before submitting"),
 },
 ];

 const steps = showAccountSelection 
 ? [
  {
   title: t("Select Accounts"),
   slug:"select-accounts",
   component: <SelectAccountsStep />,
   optional: false,
   infoTitle: t("Select Accounts"),
   infoDescription: t("Choose which sub-accounts this request applies to"),
  },
  ...baseSteps
  ]
 : baseSteps;

 return (
 <div className="p-6">
  <div className="mb-6">
   <PageHeader 
    title={t("Create Complaint Request")} 
    subtitle={t("Follow the steps below to submit a new complaint")}
    icon={ArrowLeft} 
    onIconClick={() => navigate('/dashboard/requests')} 
   />
   {consumerInfo && (
    <div className="mt-4 bg-blue-50/50 border border-blue-100 rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
     <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold">
       {consumerInfo.name.split(' ').map((n: string) => n[0]).join('').substring(0, 2)}
      </div>
      <div>
       <div className="font-bold text-gray-900 text-sm flex items-center gap-2">
        {consumerInfo.name} 
        <span className="text-gray-400 font-medium text-xs hidden sm:inline-block">({consumerInfo.accountNo})</span>
       </div>
       <div className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5">
        <MapPin className="w-3 h-3" />
        {consumerInfo.address}
       </div>
      </div>
     </div>
     </div>
    )}
    <ActiveAccountBanner />
  </div>
  
  
  {!isSubmitted ? (
   <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8 mt-2">
    <Stepper
    steps={steps}
    onStepChange={() => {}}
    onValidation={async () => ({ isValid: true })}
    onComplete={() => setIsSubmitted(true)}
    storageKey="complaint-request-draft"
    enableApiSave={false}
    saveOnStepChange={true}
    trackDirtyState={true}
    autoSave={true}
    persistProgress={true}
    allowSkip={true}
    showDraftResume={true}
    />
   </div>
  ) : (
   <SimpleSuccessScreen
    title="Complaint Submitted!"
    ticketId={`CMP-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`}
    onPrimaryClick={() => navigate('/dashboard/requests')}
    onSecondaryClick={() => {
      setIsSubmitted(false);
      window.location.reload();
    }}
   />
  )}
  </div>
 );
};
